import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, X, Sparkles, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, photoUrl } from "@/lib/api";
import FilterBar from "@/components/FilterBar";
import { RoommateCard } from "@/components/BrowseCards";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) return photoUrl(url.replace("/api/photos/", ""));
    return `${BACKEND_URL}${url}`;
}

const EMPTY = { city: "", budget_min: "", budget_max: "", move_in_after: "", gender: "Any", vibe_tags: [] };

export default function FindRoommatesTab() {
    const [filters, setFilters] = useState(EMPTY);
    const [roommates, setRoommates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.city) params.city = filters.city;
            if (filters.budget_min) params.budget_min = filters.budget_min;
            if (filters.budget_max) params.budget_max = filters.budget_max;
            if (filters.move_in_after) params.move_in_after = filters.move_in_after;
            if (filters.gender && filters.gender !== "Any") params.gender = filters.gender;
            if (filters.vibe_tags?.length) params.vibe_tags = filters.vibe_tags.join(",");
            const { data } = await api.get("/roommates", { params });
            setRoommates(data.roommates || []);
        } catch (e) {
            toast.error("Couldn't load roommates");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const swipeLike = async (profile) => {
        try {
            const { data } = await api.post("/swipe", { target_user_id: profile.user_id, direction: "like" });
            if (data.matched) {
                toast.success(`🎉 It's a match with ${profile.name}!`);
            } else {
                toast.success("Sent a like");
            }
            setDetail(null);
        } catch (e) {
            toast.error("Couldn't send like");
        }
    };

    return (
        <div className="mx-auto max-w-xl px-5 pt-10 pb-24" data-testid="roommates-tab">
            <div className="flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3 py-1.5 text-xs font-semibold w-fit">
                <Users className="h-3.5 w-3.5" strokeWidth={2.5} /> your kind of people
            </div>
            <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter"
            >
                Find your <span className="font-fancy text-gradient-purple">roommates</span>
            </motion.h1>
            <p className="mt-2 text-muted-foreground">Filter by city, budget, or vibe tags. Tap a card for more.</p>

            <div className="mt-6">
                <FilterBar filters={filters} setFilters={setFilters} showVibeTags testIdPrefix="roommates-filter" />
            </div>

            <div className="mt-4 text-xs font-semibold text-muted-foreground">
                {loading ? "Loading…" : `${roommates.length} people`}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
                {!loading && roommates.length === 0 ? (
                    <div className="col-span-2 rounded-3xl border border-border bg-card p-8 text-center" data-testid="roommates-empty">
                        <div className="mx-auto h-12 w-12 rounded-2xl bg-accent text-accent-foreground grid place-items-center">
                            <Users className="h-6 w-6" strokeWidth={2.5} />
                        </div>
                        <div className="mt-4 font-display text-lg font-bold">No one matches yet</div>
                        <p className="text-sm text-muted-foreground mt-1">Try loosening your filters.</p>
                    </div>
                ) : (
                    roommates.map((r, i) => (
                        <motion.div key={r.user_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.02 * i }}>
                            <RoommateCard profile={r} onClick={() => setDetail(r)} testId={`roommate-card-${r.user_id}`} />
                        </motion.div>
                    ))
                )}
            </div>

            <RoommateDetail profile={detail} onClose={() => setDetail(null)} onLike={swipeLike} />
        </div>
    );
}

function RoommateDetail({ profile, onClose, onLike }) {
    if (!profile) return null;
    const cover = profile.picture || (profile.photo_urls?.[0] ? toAbsolute(profile.photo_urls[0]) : null);
    return (
        <AnimatePresence>
            {profile && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-xl"
                    data-testid="roommate-detail"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl border-t border-border bg-card overflow-hidden max-h-[92vh] overflow-y-auto no-scrollbar"
                    >
                        <div className="relative aspect-[4/3] bg-gradient-to-br from-primary/20 to-accent">
                            {cover && <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <button
                                onClick={onClose}
                                data-testid="roommate-detail-close"
                                className="absolute top-3 right-3 h-9 w-9 rounded-full bg-background/90 grid place-items-center backdrop-blur"
                            >
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                            <div className="absolute top-3 left-3 rounded-full bg-background/90 backdrop-blur px-3 py-1.5 flex items-center gap-1">
                                <Sparkles className="h-3 w-3 text-primary" strokeWidth={3} />
                                <span className="font-black text-sm text-gradient-purple">{profile.match_percent ?? 0}%</span>
                            </div>
                            <div className="absolute bottom-3 left-4 right-4 text-white">
                                <div className="font-display text-3xl font-black">
                                    {profile.name}
                                    {profile.age ? <span className="font-normal">, {profile.age}</span> : null}
                                </div>
                                {profile.city && (
                                    <div className="flex items-center gap-1 text-sm opacity-90">
                                        <MapPin className="h-3.5 w-3.5" /> {profile.city}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-6">
                            {profile.bio && <p className="text-sm leading-relaxed">{profile.bio}</p>}
                            {(profile.budget_min || profile.budget_max) && (
                                <div className="mt-4 text-sm">
                                    <span className="font-semibold">Budget:</span>{" "}
                                    <span className="text-muted-foreground">
                                        ₹{(profile.budget_min || 0).toLocaleString("en-IN")} – {profile.budget_max ? "₹" + profile.budget_max.toLocaleString("en-IN") : "∞"}/mo
                                    </span>
                                </div>
                            )}
                            {!!profile.vibe_tags?.length && (
                                <div className="mt-4">
                                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vibe tags</div>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {profile.vibe_tags.map((t) => (
                                            <span key={t} className="vibe-pill">{t}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <Button
                                className="mt-6 h-14 w-full rounded-2xl font-semibold lift"
                                onClick={() => onLike(profile)}
                                data-testid="roommate-like-btn"
                            >
                                <Sparkles className="mr-2 h-5 w-5" strokeWidth={2.5} /> Like {profile.name.split(" ")[0]}
                            </Button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
